import * as R from 'ramda'
import { LOCATION_CHANGE } from 'connected-react-router'
import { ofType } from 'redux-observable'
import { empty, of, throwError } from 'rxjs'
import {
  catchError,
  delayWhen,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  merge,
  mergeMap,
  startWith,
  switchMap
} from 'rxjs/operators'
import { fetchResource } from 'api/planet/resource'
import { fetchVersion } from 'api/planet/version'
import {
  getLocationChangeOperationCode,
  getLocationChangeQuery,
  isKeyUnchanged,
  isProductPageAction
} from 'domain/router'
import { getResourceByKey } from 'domain/resources'
import { captureException, setTagsContext } from 'helpers/raven'
import * as Actions from 'store/actions/index.ts'
import { getCountryCode, getCultureCode, getSaleKey, getIsActivePackageFetched, getShouldDoubleThePriceFeatureEnabled, getLoggedIn, getOperationCode, getMarketId, getIsPreviewMode, getVersion } from 'store/selectors'
import { handleUnauthorized } from './helpers'

const handleVersionChanged = (actions$, state$) => fetchVersion(state$.value).pipe(
  map(({ response: version }) => {
    const currentVersion = getVersion(state$.value)
    if (currentVersion === version) {
      captureException(new Error('Version changed to same version'), {
        extra: { version },
        level: 'warning'
      })
    }

    return Actions.version.onNewVersion(version)
  }),
  startWith(Actions.fetch.onFetchStarted()),
  startWith(Actions.version.onVersionChanged()),
  catchError(versionFetchError => {
    if (versionFetchError.status === 401) { return handleUnauthorized(actions$, state$) }

    captureException(new Error('Could not fetch current version'), {
      extra: { versionFetchError }
    })

    return of(Actions.version.onVersionFetchError(versionFetchError))
  })
)

const fetchAndHandleResource = (
  type,
  saleKey,
  key,
  countryCode,
  cultureCode,
  marketId,
  version,
  isPreview,
  isResourceV2,
  actions$,
  state$
) => fetchResource(saleKey, key, countryCode, cultureCode, marketId, version, isPreview, isResourceV2).pipe(
  switchMap(({ response }) => {
    if (type === 'sale') {
      setTagsContext({
        saleKey: response.resourceKey
      })

      if (!response.resources[response.resourceKey].resources.length) {
        return of(Actions.resources.onEmptySaleResourcesError())
      }
    }
    return of(Actions.resources.onResourceFetched(type, saleKey, key, response))
  }),
  startWith(Actions.fetch.onFetchStarted()),
  catchError(resourceFetchError => {
    if (typeof resourceFetchError.status === 'number') {
      if (resourceFetchError.status === 0) {
        return of(Actions.errors.onNetworkError(resourceFetchError))
      }

      if (resourceFetchError.status === 401) {
        return handleUnauthorized()
      }

      if (resourceFetchError.status === 410) {
        return handleVersionChanged(actions$, state$)
      }
    }
    captureException(new Error('Unable to fetch resource'), {
      extra: { isPreview, key, resourceFetchError, saleKey, type, version }
    })
    return of(Actions.resources.onResourceFetchError(resourceFetchError, type, key))
  })
)

// type IsValidPackage = any => boolean // type IsValidPackage = SelectPackageAction => boolean
const isValidPackage = R.pipe(
  R.path(['payload', 'idPackage']),
  R.anyPass([R.isNil, R.isEmpty]),
  R.not
)

const resourcesEpic = (actions$, state$) => {
  const authentications$ = actions$.pipe(ofType(Actions.member.MEMBER_INFO_FETCHED))

  const saleKeys$ = actions$.pipe(
    ofType(LOCATION_CHANGE),
    map(action => ({
      key: getLocationChangeQuery(action).key,
      operationCode: getLocationChangeOperationCode(action),
      type: 'sale'
    })),
    distinctUntilKeyChanged('operationCode')
  )

  const productKeys$ = actions$.pipe(
    ofType(LOCATION_CHANGE),
    distinctUntilChanged(isKeyUnchanged),
    filter(isProductPageAction),
    map(action => ({
      key: getLocationChangeQuery(action).key,
      operationCode: getLocationChangeOperationCode(action),
      type: 'product'
    }))
  )

  const packageKeys$ = actions$.pipe(
    ofType(Actions.package.SELECT_PACKAGE),
    filter(action => isValidPackage(action) && !getIsActivePackageFetched(state$.value)),
    map(action => ({
      key: action.payload.idPackage,
      operationCode: getOperationCode(state$.value),
      type: 'package'
    }))
  )

  return saleKeys$.pipe(
    merge(productKeys$),
    merge(packageKeys$),
    delayWhen(() => getLoggedIn(state$.value)
      ? of({})
      : authentications$
    ),
    mergeMap(({ type, key }) => {
      const state = state$.value

      const countryCode = getCountryCode(state)
      const cultureCode = getCultureCode(state)
      const isPreview = getIsPreviewMode(state)
      const marketId = getMarketId(state)
      const saleKey = getSaleKey(state)
      const resource = getResourceByKey(state, key)
      const saleVersion = getVersion(state)
      const isResourceV2 = getShouldDoubleThePriceFeatureEnabled(state)

      if (!saleVersion) {
        const errorMessage = 'There is no version or could not fetch current version'
        captureException(new Error(errorMessage), {
          extra: { saleVersion }
        })

        return throwError(errorMessage).pipe(
          catchError(versionError => of(Actions.version.onVersionFetchError(versionError)))
        )
      }

      if (!countryCode || !cultureCode) { return empty() }

      return (resource && resource.fetched)
        ? empty()
        : (type === 'sale')
            ? fetchAndHandleResource(type, saleKey, saleKey, countryCode, cultureCode, marketId, saleVersion, isPreview, isResourceV2, actions$, state$)
            : fetchAndHandleResource(type, saleKey, key, countryCode, cultureCode, marketId, saleVersion, isPreview, isResourceV2, actions$, state$)
    })
  )
}

export default resourcesEpic
